import { useCallback, useMemo } from "react"

import type { CollectionKind } from "~/utils/collectionKind"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useFraming } from "~/components/demand-2/useDemandIntents"
import { useSetFramingAnswer } from "~/components/demand-2/shared/FramingAnswerPicker"
import {
  type AnswerScope,
  effectiveAnswerFor,
  framingAnswerValue,
  FRAMING_QUESTIONS,
  scopeWriteKey,
  valuedAnswer,
  WHOLE_SCOPE,
} from "~/utils/demandFraming"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { api } from "~/utils/api"
import type { InsightScalars } from "~/utils/demandInsightValues"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import {
  computeFashionPreorderDevelopment,
  DEFAULT_FASHION_PREORDER_MODE,
  type FashionPreorderCollection,
  type FashionPreorderDevelopmentFigures,
  paceCurveFor,
} from "~/utils/demandFashionPreorderDevelopment"
import {
  type AdaptationMode,
  adaptationSchema,
  computeNoosPreorderDevelopment,
  type NoosPreorderFigures,
} from "~/utils/demandPreorderDevelopment"

/**
 * Both kinds are engine-applied: each variant is projected on its own book under its answer
 * (`fashionPreorderUnitsFor`, `noosPreorderUnitsFor`), so Total moves with the answer. Both read
 * the engine's figures, so row, chart and plan agree.
 */

/** Display order, and the list the pinned y-axis ceiling is sized against. */
export const ADAPTATION_ORDER: AdaptationMode[] = [
  "closeBook",
  "maintain",
  "adaptChange",
  "adaptTrend",
]

/** The answers as both kinds list them: "Match baseline" last, as it ignores the book. */
export const ADAPTATION_ANSWER_ORDER: readonly AdaptationMode[] = [
  "closeBook",
  "adaptChange",
  "adaptTrend",
  "maintain",
]

/**
 * Each answer reads the same against either baseline, a season's comparables or a NOOS variant's own
 * reads, except `closeBook`: NOOS has no season whose book closes. A function: `formatName` reads
 * tenant naming at call time.
 */
export const adaptationLabels = (kind: CollectionKind | null): Record<AdaptationMode, string> => ({
  closeBook: kind === "noos" ? `Do not expect ${formatName("preorder", true)}` : "Close the book",
  maintain: "Match baseline",
  adaptChange: "Maintain expectations",
  adaptTrend: "Follow the trend",
})

export type PreorderDevelopment = NoosPreorderFigures & {
  kind: "noos"
  mode: AdaptationMode
  /** Whether `mode` is stored rather than the default; figures use `mode` either way. */
  modeAnswered: boolean
  scope: AnswerScope
  modeInherited: boolean
  setMode: (mode: AdaptationMode) => void
  clearMode: () => void
  /** Set the share of the pre-orders treated as known by now; null returns to the profile's own. */
  setKnownShareOverride: (share: number | null) => void
  currentWeek: Date
  isLoading: boolean
}

/**
 * The NOOS reading, off the engine's own books (`InsightScalars.preorderBook`): what the baseline's
 * reads realised and had confirmed at the same lead time, against what is confirmed now. One hook for
 * the row and the breakdown, so they can't disagree, and the same books the engine sizes the insight on.
 */
export function usePreorderDevelopment(
  variantIds: readonly string[],
  scalarsFor: (variantId: string) => InsightScalars | null | undefined,
  scope: AnswerScope = WHOLE_SCOPE,
  isLoading = false,
): PreorderDevelopment {
  const { stored: storedMode, own, setMode, clearMode } = useAdaptationAnswer(scope)
  const [overrideShare, setKnownShareOverride] = useKnownShareAnswer(scope)
  const mode = storedMode ?? DEFAULT_FASHION_PREORDER_MODE
  // A fresh Date per render defeats every memo below it, and the week boundary cannot move mid-session.
  const currentWeek = useMemo(() => getStartOfCurrentWeekUTC(), [])
  const books = useMemo(
    () => variantIds.flatMap((id) => scalarsFor(id)?.preorderBook ?? []),
    [variantIds, scalarsFor],
  )
  const { data: profile } = api.demand2.getNoosPreorderKnownShares.useQuery()
  const figures = useMemo(
    () => computeNoosPreorderDevelopment(books, mode, currentWeek, overrideShare, profile),
    [books, mode, currentWeek, overrideShare, profile],
  )

  return useMemo(
    () => ({
      kind: "noos" as const,
      ...figures,
      mode,
      modeAnswered: storedMode != null,
      scope,
      modeInherited: storedMode != null && !own,
      setMode,
      clearMode,
      setKnownShareOverride,
      currentWeek,
      isLoading,
    }),
    [
      figures,
      mode,
      storedMode,
      scope,
      own,
      setMode,
      clearMode,
      setKnownShareOverride,
      currentWeek,
      isLoading,
    ],
  )
}

/**
 * The adaptation answer at one scope (`DemandFramingAnswer`, question `preorderDevelopment`). A
 * node or variant resolves through the scopes above it; `own` = stored at this scope itself.
 * `stored` is null while nothing is stored up the chain; the caller falls back to
 * `DEFAULT_FASHION_PREORDER_MODE` (the question's default option is NOOS wording).
 */
const useAdaptationAnswer = (
  scope: AnswerScope = WHOLE_SCOPE,
): {
  stored: AdaptationMode | null
  own: boolean
  setMode: (mode: AdaptationMode) => void
  clearMode: () => void
} => {
  const framing = useFraming()
  // The route's first collection, never a group id (which resolves no workspace); the same id every
  // other answer is written against.
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0] ?? 0
  const set = useSetFramingAnswer(collectionId)
  const resolved = effectiveAnswerFor(framing, "preorderDevelopment", scope)
  const parsed = adaptationSchema.safeParse(resolved.isDefault ? null : resolved.answer)
  const key = useMemo(() => scopeWriteKey(scope), [scope])
  const setMode = useCallback(
    (mode: AdaptationMode) =>
      set.mutate({ collectionId, question: "preorderDevelopment", ...key, answer: mode }),
    [collectionId, set, key],
  )
  const clearMode = useCallback(
    () => set.mutate({ collectionId, question: "preorderDevelopment", ...key, answer: null }),
    [collectionId, set, key],
  )
  return {
    stored: parsed.success ? parsed.data : null,
    own: parsed.success && !resolved.inherited,
    setMode,
    clearMode,
  }
}

/**
 * The planner's share of the season treated as booked by now, 0–1, or null while the collection's
 * pre-order curve is in force. Stored beside the adaptation answer (`preorderKnownShare`).
 */
const useKnownShareAnswer = (
  scope: AnswerScope = WHOLE_SCOPE,
): [number | null, (share: number | null) => void] => {
  const framing = useFraming()
  // The route's first collection, as in `useAdaptationAnswer`.
  const { collectionIds } = useDemand2Scope()
  const collectionId = collectionIds[0] ?? 0
  const set = useSetFramingAnswer(collectionId)
  const resolved = effectiveAnswerFor(framing, "preorderKnownShare", scope)
  const stored = resolved.isDefault ? undefined : resolved.answer
  const pct = framingAnswerValue("preorderKnownShare", stored)
  const key = useMemo(() => scopeWriteKey(scope), [scope])
  const setKnownShareOverride = useCallback(
    (share: number | null) =>
      set.mutate({
        collectionId,
        question: "preorderKnownShare",
        ...key,
        answer:
          share == null
            ? null
            : valuedAnswer(KNOWN_SHARE_OPTION.id, share * 100, KNOWN_SHARE_OPTION.value),
      }),
    [collectionId, set, key],
  )
  return [stored == null || pct == null ? null : pct / 100, setKnownShareOverride]
}

const NOTHING_UNMEASURED = { variants: 0, booked: 0, known: 0 }

/** Its value spec is the range and precision the router accepts, so writes clamp to exactly that. */
export const KNOWN_SHARE_OPTION = FRAMING_QUESTIONS.preorderKnownShare.options.find(
  (o) => o.id === "share",
)!

export type FashionPreorderDevelopment = FashionPreorderDevelopmentFigures & {
  kind: "fashion"
  mode: AdaptationMode
  /** Whether `mode` is stored rather than the season's fallback; figures use `mode` either way. */
  modeAnswered: boolean
  /** Plan, node or variant; `modeInherited` = its `mode` is a wider scope's stored answer. */
  scope: AnswerScope
  modeInherited: boolean
  setMode: (mode: AdaptationMode) => void
  /** Drop this scope's own answer so it follows the wider one again. A no-op on the whole plan. */
  clearMode: () => void
  /** Set the share of the season treated as normally booked by now; null returns to the curve's own. */
  setKnownShareOverride: (share: number | null) => void
  /** The plan's collections with the pre-order curve each reads, for the week-by-week view. */
  collections: FashionPreorderCollection[]
  /** The route's expected-known share week by week, one line over however many drops it spans. */
  pace: { weekStart: string; fractionKnown: number }[]
  /** Confirmed book the figures leave out: styles with no comparable to measure against. */
  unmeasured: { variants: number; booked: number; known: number }
  currentWeek: Date
  isLoading: boolean
}

/**
 * The fashion reading: accepted comparables' pre-orders as the season's expected total, and the
 * collection's pre-order curve for how much should be booked by now. Same answers and storage as
 * NOOS; a separate hook because it reads a different procedure.
 */
export function useFashionPreorderDevelopment(
  collectionIds: number[],
  enabled: boolean,
  /** One hierarchy node's or one variant's rows; omitted reads the whole plan. */
  variantIds?: string[],
  /** Must name the scope `variantIds` narrows to, or one node's picker moves every node. */
  scope: AnswerScope = WHOLE_SCOPE,
  /** A slice's share of each variant's baseline (one number for all); the engine spreads by it. */
  sliceShare: number | ((variantId: string) => number) = 1,
): FashionPreorderDevelopment {
  const { stored: storedMode, own, setMode, clearMode } = useAdaptationAnswer(scope)
  const [overrideShare, setKnownShareOverride] = useKnownShareAnswer(scope)
  const { data, isLoading } = api.demand2.getFashionPreorderDevelopment.useQuery(
    { collectionIds, variantIds },
    { enabled: enabled && collectionIds.length > 0 },
  )
  const currentWeek = useMemo(() => getStartOfCurrentWeekUTC(), [])
  const mode = storedMode ?? DEFAULT_FASHION_PREORDER_MODE
  const rows = useMemo(
    () =>
      sliceShare === 1
        ? data?.rows
        : data?.rows.map((r) => {
            const share = typeof sliceShare === "number" ? sliceShare : sliceShare(r.variantId)
            return {
              ...r,
              comparables: r.comparables * share,
              baselinePreorders: r.baselinePreorders * share,
              known: r.known * share,
            }
          }),
    [data?.rows, sliceShare],
  )
  const figures = useMemo(
    () => computeFashionPreorderDevelopment(rows, mode, overrideShare),
    [rows, mode, overrideShare],
  )
  const pace = useMemo(
    () =>
      paceCurveFor(
        data?.collections ?? [],
        new Map((data?.comparablesByCollection ?? []).map((c) => [c.collectionId, c.comparables])),
      ),
    [data?.collections, data?.comparablesByCollection],
  )

  return useMemo(
    () => ({
      kind: "fashion" as const,
      ...figures,
      mode,
      modeAnswered: storedMode != null,
      scope,
      modeInherited: storedMode != null && !own,
      setMode,
      clearMode,
      setKnownShareOverride,
      collections: data?.collections ?? [],
      pace,
      unmeasured: data?.unmeasured ?? NOTHING_UNMEASURED,
      currentWeek,
      isLoading: enabled && collectionIds.length > 0 && isLoading,
    }),
    [
      figures,
      mode,
      storedMode,
      scope,
      own,
      setMode,
      clearMode,
      setKnownShareOverride,
      data?.collections,
      pace,
      data?.unmeasured,
      currentWeek,
      enabled,
      collectionIds.length,
      isLoading,
    ],
  )
}

const variantCount = (n: number) => (n === 1 ? "1 variant" : `${formatNumber(n)} variants`)

/**
 * Warns that confirmed pre-orders, or whole variants, sit outside the reading for want of a
 * comparable. Null when nothing is left out that the planner could act on.
 */
export const unmeasuredNote = (
  unmeasured: FashionPreorderDevelopment["unmeasured"],
  measuredAny: boolean,
): string | null => {
  const preorders = formatName("preorder", true)
  const fix = "Match comparables, or mark them as having none, to include them."
  const units = formatNumber(Math.round(unmeasured.known))
  if (measuredAny) {
    if (unmeasured.known <= 0) return null
    return `${units} confirmed ${preorders} on ${variantCount(unmeasured.booked)} are left out: they have no comparable to measure against. ${fix}`
  }
  if (unmeasured.variants <= 0) return null
  if (unmeasured.known > 0)
    return `${units} ${preorders} are confirmed on ${variantCount(unmeasured.booked)}, but none can be measured yet: no variant has a comparable to measure against. ${fix}`
  if (unmeasured.variants === 1)
    return `This variant has no comparable to measure ${preorders} against. ${fix}`
  return `None of the ${variantCount(unmeasured.variants)} has a comparable to measure ${preorders} against. ${fix}`
}

/** Either kind's insight, as the row and the breakdown switch on it. */
export type PreorderInsight = PreorderDevelopment | FashionPreorderDevelopment

/**
 * `data` with each answer's projection replaced by the engine's units for it
 * (`getPreorderSlicePreview`), stated against the same baseline the card measures from.
 */
export const withEnginePreview = <T extends PreorderInsight>(
  data: T,
  unitsByMode: Record<AdaptationMode, number>,
): T => {
  const base = data.kind === "fashion" ? data.totals.baselinePreorders : data.totals.lastYear
  const projectedByMode = Object.fromEntries(
    Object.entries(unitsByMode).map(([m, units]) => [m, base + units]),
  ) as Record<AdaptationMode, number>
  const projected = projectedByMode[data.mode]
  return { ...data, projectedByMode, projected, development: projected - data.totals.knownNow }
}

/** Every answer moves the same: the slice has nothing an answer could reach. */
export const answersMoveNothing = (unitsByMode: Record<AdaptationMode, number>): boolean => {
  const [first = 0, ...rest] = Object.values(unitsByMode)
  return rest.every((units) => Math.abs(units - first) < 0.5)
}

/**
 * The Settings row's numbers for either kind: projection, basis, gap. On fashion the basis is the
 * pre-orders the baseline carries, not the comparables' whole book, because that is what the engine
 * applies; the two differ only while the comparable holds undelivered orders.
 */
export type PreorderRowFigures = {
  projected: number
  basis: number
  vsBasis: number
  vsBasisPct: number | null
  /** Whether there is anything to project at all; an insight without a basis states what it would need. */
  hasBasis: boolean
}

export const preorderRowFigures = (insight: PreorderInsight): PreorderRowFigures =>
  insight.kind === "noos"
    ? {
        projected: insight.projected,
        basis: insight.totals.lastYear,
        vsBasis: insight.vsLastYear,
        vsBasisPct: insight.vsLastYearPct,
        hasBasis: insight.totals.lastYear > 0 || insight.projected > 0,
      }
    : {
        projected: insight.projected,
        basis: insight.totals.baselinePreorders,
        vsBasis: insight.vsBaseline,
        vsBasisPct: insight.vsBaselinePct,
        hasBasis: insight.totals.comparables > 0 || insight.totals.knownNow > 0,
      }
